// src/pages/ResearchPaper.tsx

import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Grid,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WifiIcon from "@mui/icons-material/Wifi";
import StorefrontIcon from "@mui/icons-material/Storefront";
import InsightsIcon from "@mui/icons-material/Insights";

const palette = {
  bg: "linear-gradient(160deg, #f0fdf4 0%, #eff6ff 60%, #faf5ff 100%)",
  primary: "#0F172A",
  blue: "#2563EB",
  green: "#10B981",
  gradientMain: "linear-gradient(90deg, #10B981, #2563EB)",
  gradientSoft:
    "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(37,99,235,0.08))",
  textPrimary: "#0f172a",
  textSecondary: "#475569",
  border: "#e2e8f0",
};

const KEYFRAMES = `
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.fade-up {
  opacity: 0;
  animation: fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) forwards;
}
.d0 { animation-delay: 0ms; }
.d1 { animation-delay: 120ms; }
.d2 { animation-delay: 240ms; }
.d3 { animation-delay: 360ms; }
.d4 { animation-delay: 480ms; }
`;

/* ===== SECTION TITLE ===== */
const SectionTitle = ({ title }: { title: string }) => (
  <>
    <Typography
      sx={{
        fontWeight: 900,
        fontSize: "1.5rem",
        fontFamily: "'Playfair Display', serif",
        mb: 1,
        background: "linear-gradient(90deg, #0ea5e9, #10b981)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ mb: 2.5 }} />
  </>
);

/* ===== STAT BADGE ===== */
const StatBadge = ({
  value,
  label,
}: {
  value: string;
  label: string;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      px: 3,
      py: 2,
      borderRadius: 3,
      background:
        "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(37,99,235,0.1))",
      border: "1px solid rgba(16,185,129,0.2)",
      minWidth: 110,
    }}
  >
    <Typography
      sx={{
        fontFamily: "'Playfair Display', serif",
        fontWeight: 900,
        fontSize: "1.8rem",
        background: "linear-gradient(90deg, #10b981, #2563eb)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: "DM Sans, sans-serif",
        fontSize: "0.7rem",
        color: "#64748b",
        textAlign: "center",
        mt: 0.4,
        fontWeight: 600,
        letterSpacing: 0.3,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
  </Box>
);

/* ===== CARD STYLE ===== */
const cardSx = {
  p: 3,
  borderRadius: 4,
  mb: 2,
  border: `1px solid ${palette.border}`,
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(14px)",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 20px 40px rgba(16,185,129,0.13)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: 5,
  },
};

const bodyText = {
  fontFamily: "'DM Sans', sans-serif",
  color: "#334155",
  lineHeight: 1.85,
  fontSize: "0.97rem",
};

const sectionHeader = {
  fontWeight: 900,
  fontSize: "1.7rem",
  fontFamily: "'Playfair Display', serif",
  background: "linear-gradient(90deg, #0ea5e9, #10b981)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const dividerStyle = {
  width: 100,
  mx: "auto",
  mt: 1.5,
  height: 3,
  borderRadius: 2,
  background: "linear-gradient(90deg, #0ea5e9, #10b981)",
};

/* ===== FINDINGS DATA ===== */
const findings = [
  {
    icon: <TrendingUpIcon sx={{ color: "#10B981", fontSize: 26 }} />,
    title: "Explosive UPI Growth",
    desc: "UPI transaction volumes grew from 0.3 billion in 2016 to 171 billion in 2025 — an annualised growth rate rarely seen in traditional financial infrastructure, driven by network effects and rapid smartphone adoption.",
    tag: "Macro Trend",
  },
  {
    icon: <AccountBalanceIcon sx={{ color: "#2563EB", fontSize: 26 }} />,
    title: "Financial Inclusion Deepened",
    desc: "92.5% of surveyed respondents actively use UPI. Around 1 in 6 identified a UPI-linked platform as their very first meaningful interaction with formal banking — shifting inclusion from nominal to functional.",
    tag: "Inclusion",
  },
  {
    icon: <SwapHorizIcon sx={{ color: "#8B5CF6", fontSize: 26 }} />,
    title: "Cash Displaced by Digital",
    desc: "Pre-adoption, digital transactions averaged 28.6% of total payments. Post-adoption, this rose to 71.3% — a jump of 42.7 percentage points — with 63.2% of users reporting a significant reduction in cash reliance.",
    tag: "Behaviour",
  },
  {
    icon: <StorefrontIcon sx={{ color: "#F59E0B", fontSize: 26 }} />,
    title: "MSMEs Benefit Most",
    desc: "Among business respondents, 74.3% reported greater willingness to accept digital payments and 58.6% saw improved transaction record-keeping — strengthening their integration into formal financial systems.",
    tag: "Enterprise",
  },
  {
    icon: <PeopleAltIcon sx={{ color: "#EC4899", fontSize: 26 }} />,
    title: "Access vs. Usage Gap",
    desc: "Bank account ownership was near-universal in the sample, yet only ~3 in 4 actively engaged with formal banking before UPI. Meaningful inclusion requires not just access, but active, sustained usage.",
    tag: "Policy Gap",
  },
  {
    icon: <WarningAmberIcon sx={{ color: "#EF4444", fontSize: 26 }} />,
    title: "Digital Divide Persists",
    desc: "Rural, elderly, and low-income segments face barriers including smartphone access, connectivity, and digital literacy. Adoption statistics may overstate true inclusion without complementary infrastructure reforms.",
    tag: "Risk",
  },
  {
    icon: <InsightsIcon sx={{ color: "#0EA5E9", fontSize: 26 }} />,
    title: "Formalisation Through Traceability",
    desc: "The Transaction Formalisation Index (mean = 0.67) showed that digital payments create traceable records for households and MSMEs, improving their visibility to banks, fintechs, and formal credit providers.",
    tag: "Regression",
  },
  {
    icon: <WifiIcon sx={{ color: "#14B8A6", fontSize: 26 }} />,
    title: "Infrastructure Is the Foundation",
    desc: "UPI's effectiveness is contingent on network reliability, device access, and backend banking stability. Cybersecurity, fraud risks, and pricing sustainability are key structural challenges requiring policy attention.",
    tag: "Infrastructure",
  },
];

/* ===== MAIN COMPONENT ===== */
const ResearchPaper = () => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: palette.bg,
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, md: 10 },
      }}
    >
      <style>{KEYFRAMES}</style>

      {/* ===== BACKGROUND DECORATIONS ===== */}
      <Typography
        sx={{
          position: "absolute",
          fontSize: "18rem",
          fontFamily: "'Playfair Display', serif",
          fontWeight: 900,
          opacity: 0.25,
          top: -20,
          left: -10,
          background: palette.gradientMain,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        ₹
      </Typography>

      <Typography
        sx={{
          position: "absolute",
          fontSize: "11rem",
          fontFamily: "'Playfair Display', serif",
          fontWeight: 900,
          opacity: 0.25,
          bottom: 80,
          right: 40,
          color: palette.blue,
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        %
      </Typography>

      <Typography
        sx={{
          position: "absolute",
          bottom: "8%",
          left: "6%",
          fontSize: "8rem",
          fontFamily: "'Playfair Display', serif",
          fontWeight: 900,
          opacity: 0.25,
          letterSpacing: 8,
          background: palette.gradientMain,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        UPI
      </Typography>

      {/* ₹ (top right small) */}
<Typography
  sx={{
    position: "absolute",
    top: "15%",
    right: "5%",
    fontSize: "6rem",
    fontWeight: 900,
    opacity: 0.25,
    color: palette.green,
    pointerEvents: "none",
  }}
>
  ₹
</Typography>

{/* $ symbol */}
<Typography
  sx={{
    position: "absolute",
    bottom: "20%",
    left: "10%",
    fontSize: "7rem",
    fontWeight: 900,
    opacity: 0.25,
    color: palette.blue,
    pointerEvents: "none",
  }}
>
  $
</Typography>

{/* DATA text */}
<Typography
  sx={{
    position: "absolute",
    top: "40%",
    left: "5%",
    fontSize: "5rem",
    fontWeight: 900,
    opacity: 0.25,
    letterSpacing: 6,
    color: palette.primary,
    pointerEvents: "none",
  }}
>
  DATA
</Typography>

{/* SMALL CIRCLE */}
<Box
  sx={{
    position: "absolute",
    top: "60%",
    right: "20%",
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: palette.gradientMain,
    opacity: 0.08,
    pointerEvents: "none",
  }}
/>

{/* SQUARE */}
<Box
  sx={{
    position: "absolute",
    top: "25%",
    left: "25%",
    width: 50,
    height: 50,
    borderRadius: 2,
    background: palette.blue,
    opacity: 0.25,
    transform: "rotate(25deg)",
    pointerEvents: "none",
  }}
/>

{/* LINE */}
<Box
  sx={{
    position: "absolute",
    bottom: "15%",
    left: "35%",
    width: 120,
    height: 2,
    background: palette.gradientMain,
    opacity: 0.25,
    pointerEvents: "none",
  }}
/>

{/* FLOATING DOT GRAPH */}
<Box
  sx={{
    position: "absolute",
    top: "55%",
    right: "8%",
    opacity: 0.25,
    pointerEvents: "none",
  }}
>
  <svg width="120" height="80">
    <circle cx="10" cy="60" r="4" fill="#10B981" />
    <circle cx="40" cy="40" r="4" fill="#10B981" />
    <circle cx="70" cy="50" r="4" fill="#3B82F6" />
    <circle cx="100" cy="20" r="4" fill="#2563EB" />
  </svg>
</Box>

<Typography
  sx={{
    position: "absolute",
    bottom: "5%",
    right: "25%",
    fontSize: "3rem",
    fontWeight: 800,
    opacity: 0.25,
    color: palette.primary,
    letterSpacing: 4,
    pointerEvents: "none",
  }}
>
  GDP
</Typography>

<Typography
  sx={{
    position: "absolute",
    top: "70%",
    left: "12%",
    fontSize: "3rem",
    fontWeight: 800,
    opacity: 0.25,
    color: palette.primary,
    letterSpacing: 4,
    pointerEvents: "none",
  }}
>
  UPI
</Typography>

      <Box
        sx={{
          position: "absolute",
          right: "6%",
          bottom: "10%",
          opacity: 0.25,
          pointerEvents: "none",
        }}
      >
        <svg width="280" height="180">
          <path
            d="M20 150 L70 90 L120 110 L190 40"
            stroke="url(#grad)"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="20" cy="150" r="5" fill="#10B981" />
          <circle cx="70" cy="90" r="5" fill="#10B981" />
          <circle cx="120" cy="110" r="5" fill="#3B82F6" />
          <circle cx="190" cy="40" r="5" fill="#2563EB" />
          <defs>
            <linearGradient id="grad">
              <stop offset="0%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
          </defs>
        </svg>
        
      </Box>

      <Container maxWidth="lg">

        {/* ===== TITLE ===== */}
        <Box textAlign="center" mb={6}  px={{ xs: 2, sm: 0 }} className="fade-up d0">
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              fontWeight: 900,
              fontSize: { xs: "1.6rem", sm: "2rem", md: "3.2rem" },
              lineHeight: 1.18,
              background: "linear-gradient(90deg, #6f92e4, #3d5eb9)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",  
              mb: 2.5,
            }}
          >
            UPI Adoption, Financial Inclusion &
            Digital Transaction Formalisation in India
          </Typography>

          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              color: "#475569",
              maxWidth: 680,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: "1rem",
              mb: 4,
            }}
          >
            An evidence-based investigation into how India's Unified Payments Interface
            reshapes financial inclusion, formalises cash-based transactions, and
            drives participation in the formal economy — across households, MSMEs,
            and informal sector participants.
          </Typography>

          {/* STATS ROW */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
              mt: 1,
            }}
          >
            <StatBadge value="114" label="Survey respondents" />
            <StatBadge value="171B" label="UPI txns in 2025" />
            <StatBadge value="42.7pp" label="Digital txn shift" />
            <StatBadge value="R²=0.48" label="Model fit (combined)" />
          </Box>
        </Box>

        {/* ===== EXECUTIVE SUMMARY ===== */}
        <Paper sx={cardSx} className="fade-up d0">
          <SectionTitle title="Executive Summary" />
          <Typography sx={bodyText}>
            Since its launch in 2016, India's Unified Payments Interface (UPI) has become the country's dominant retail payment platform — processing over 171 billion transactions in 2025 alone. This study examines whether UPI adoption has translated into <strong>meaningful financial inclusion</strong> and <strong>transaction formalisation</strong>, not just convenience.
          </Typography>
          <Typography sx={{ ...bodyText, mt: 2 }}>
            Using a mixed-methods design — a structured survey of 114 respondents, composite index construction, OLS regression, and national secondary data — the research finds strong, statistically significant associations between UPI adoption intensity, engagement with formal financial services, and an increasing share of digitally recorded transactions. Financial inclusion is found to partially mediate the relationship between digital payment adoption and formalisation.
          </Typography>
        </Paper>

        {/* ===== ABSTRACT ===== */}
        <Paper sx={cardSx} className="fade-up d0">
          <SectionTitle title="Abstract" />
          <Typography sx={bodyText}>
            This study investigates the relationship between UPI adoption, financial inclusion, and transaction formalisation in India. It focuses on households, MSMEs, and informal sector participants — groups for whom digital transaction records may substantially improve financial visibility and access to institutional credit.
          </Typography>
          <Typography sx={{ ...bodyText, mt: 2 }}>
            Using primary survey evidence, secondary macroeconomic data (NPCI, RBI, World Bank), and regression-based analysis, the study positions UPI as a <em>digital infrastructure channel</em> that supports inclusion and formalisation through four mechanisms: lower transaction costs, digital identity creation, improved transaction visibility, and stronger links to formal financial institutions. India's UPI experience is also situated within an international context, drawing comparisons with Kenya's M-Pesa and Southeast Asian mobile payment systems.
          </Typography>
        </Paper>

        {/* ===== KEY FINDINGS HEADER ===== */}
        <Box textAlign="center" mb={5} className="fade-up d0">
          <Typography sx={sectionHeader}>Key Findings</Typography>
          <Divider sx={dividerStyle} />
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              color: "#64748b",
              mt: 2,
              fontSize: "0.95rem",
              maxWidth: 560,
              mx: "auto",
            }}
          >
            Eight core findings derived from primary survey data, composite index analysis, OLS regression, and national macro-level trends.
          </Typography>
        </Box>

        {/* ===== FINDINGS GRID ===== */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {findings.map((item, i) => (
            <Grid item xs={12} md={6} key={i} className="fade-up d0">
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid #dbeafe",
                  background: "rgba(255,255,255,0.82)",
                  backdropFilter: "blur(12px)",
                  transition: "all 0.3s ease",
                  height: "100%",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 20px 40px rgba(14,165,233,0.13)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(37,99,235,0.1))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 800,
                          fontSize: "1rem",
                          color: "#0f172a",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Chip
                        label={item.tag}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          fontFamily: "'DM Sans', sans-serif",
                          background:
                            "linear-gradient(90deg, rgba(16,185,129,0.13), rgba(37,99,235,0.13))",
                          color: "#334155",
                          border: "1px solid rgba(16,185,129,0.2)",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Typography sx={{ ...bodyText, fontSize: "0.9rem" }}>
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        {/* ===== CONCLUSION ===== */}
        <Paper sx={{ ...cardSx, mb: 6 }} className="fade-up d0">
          <SectionTitle title="Conclusion" />
          <Typography sx={bodyText}>
            This study concludes that <strong>UPI adoption is positively and significantly associated with financial inclusion and transaction formalisation</strong> in India. Individuals who use UPI more intensively show stronger engagement with formal financial institutions and a markedly higher share of digitally recorded transactions.
          </Typography>
          <Typography sx={{ ...bodyText, mt: 2 }}>
            UPI has evolved beyond a payment convenience into digital financial infrastructure — reducing transaction costs, generating traceable records, and encouraging active participation in formal financial systems. For MSMEs and informal workers, this translates into improved visibility, better record-keeping, and potential access to formal credit.
          </Typography>
          <Typography sx={{ ...bodyText, mt: 2 }}>
            However, these effects are <strong>partial and uneven</strong>. Digital literacy gaps, infrastructure disparities, cybersecurity risks, and unequal regional access mean that high adoption statistics alone do not guarantee deep inclusion. Long-term progress depends on complementary policy reforms in financial literacy, consumer protection, and targeted outreach to rural and underserved populations.
          </Typography>
        </Paper>

        {/* ===== VIEW FULL PAPER DROPDOWN ===== */}
        <Box mt={2}>
          <Box
            onClick={() => setOpen(!open)}
            sx={{
              cursor: "pointer",
              p: 3.5,
              borderRadius: 4,
              border: `1px solid ${palette.border}`,
              background:
                "linear-gradient(135deg, rgba(14,165,233,0.12), rgba(16,185,129,0.12))",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 15px 40px rgba(16,185,129,0.18)",
              },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: "1rem",
                  color: "#0f172a",
                }}
              >
                {open ? "Hide Full Research Paper" : "📄 View Full Research Paper"}
              </Typography>
              {!open && (
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "0.8rem",
                    color: "#64748b",
                    mt: 0.3,
                  }}
                >
                  Abstract · Literature Review · Data · Methodology · Analysis · References
                </Typography>
              )}
            </Box>
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.3s",
                color: "#10B981",
              }}
            />
          </Box>

          <Box
            sx={{
              height: open ? "900px" : 0,
              opacity: open ? 1 : 0,
              overflow: "hidden",
              transition: "all 0.4s ease",
              mt: open ? 2 : 0,
            }}
          >
            <iframe
              src="/paper.pdf"
              width="100%"
              height="100%"
              style={{
                border: "none",
                borderRadius: "16px",
              }}
            />
          </Box>
        </Box>

      </Container>
    </Box>
  );
};

export default ResearchPaper;